// /src/views/AffiliateDashboard/index.js
import { useState, useEffect, useRef } from "react";
import { Card, Grid, Select, MenuItem } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { collection, query, where, getDocs, onSnapshot, getDoc, doc } from "firebase/firestore";
import { db } from "../../firebaseConfig";

import StatsCards from "./components/StatsCard";
import Charts from "./components/Charts";
import CodeList from "./components/CodeList";
import PayoutRequest from "./components/PayoutRequest";
import TierProgress from "./components/Charts/TierProgress";
import PayoutTable from "./components/Tables/PayoutTable";
import { mockData } from "./components/mockData";

function AffiliateDashboard() {
  const [useMockData] = useState(false);

  const [stats, setStats] = useState({
    totalEarnings: 0, // displayed lifetime earnings (payout sum)
    commissionTotal: 0,
    activeReferrals: 0,
    monthlyRevenue: 0,
    availableForPayout: 0,
    lifetimeEarnings: 0,

    referralsByTier: { tier1: 0, tier2: 0, tier3: 0 },
    currentTier: 1,

    // ✅ Year buckets (12 months each)
    byYear: {},

    // ✅ Defaults (current year)
    monthlySignups: Array(12).fill(0),
    monthlyPlanUses: Array(12).fill(0),
    annualPlanUses: Array(12).fill(0),
    annualSignups: Array(12).fill(0),
    monthlyEarnings: Array(12).fill(0),
    tier1Earnings: Array(12).fill(0),
    tier2Earnings: Array(12).fill(0),
    tier3Earnings: Array(12).fill(0),

    // ✅ NEW: monthly-only active stats (do NOT include annuals)
    activeMonthlyReferrals: 0,
    activeMonthlyReferralsByTier: { tier1: 0, tier2: 0, tier3: 0 },
    activeMonthlyRecurringCommissionTotal: 0,
  });

  const [codes, setCodes] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const payoutsRef = useRef([]);

  const [isAdmin, setIsAdmin] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState(null);

  const [error, setError] = useState(null);
  const [paypalEmail, setPaypalEmail] = useState("");

  const userId = localStorage.getItem("userID");
  const viewingUserId = isAdmin && selectedAffiliateId ? selectedAffiliateId : userId;

  useEffect(() => {
    const ADMIN_UID = process.env.REACT_APP_ADMIN_UID || "4JkJV2u5InQeearkcPKfFkbC73s2";
    setIsAdmin(userId === ADMIN_UID);
  }, [userId]);

  useEffect(() => {
    const fetchAffiliates = async () => {
      if (!isAdmin) return;
      try {
        const affiliatesRef = collection(db, "Users");
        const affiliatesSnap = await getDocs(query(affiliatesRef, where("affiliate", "==", true)));
        const affiliatesData = affiliatesSnap.docs.map((d) => ({
          id: d.id,
          email: d.data().profileInfo?.email || d.data().email || "No email found",
        }));
        setAffiliates(affiliatesData);
      } catch (err) {
        console.error("Error fetching affiliates:", err);
        setError("Failed to load affiliates");
      }
    };
    fetchAffiliates();
  }, [isAdmin]);

  useEffect(() => {
    const fetchPaypalEmail = async () => {
      try {
        const userDoc = await getDoc(doc(db, "Users", viewingUserId));
        if (userDoc.exists()) setPaypalEmail(userDoc.data().paypalEmail || "");
      } catch (err) {
        console.error("Error fetching PayPal email:", err);
      }
    };
    if (viewingUserId) fetchPaypalEmail();
  }, [viewingUserId]);

  // parse subscription createdAt in all common formats
  const parseSubDate = (createdAt) => {
    if (!createdAt) return null;
    if (createdAt?.seconds) return new Date(createdAt.seconds * 1000);
    if (typeof createdAt === "object" && typeof createdAt.toDate === "function") return createdAt.toDate();
    const d = new Date(createdAt);
    return Number.isFinite(d.getTime()) ? d : null;
  };

  const ensureYear = (byYear, year) => {
    if (byYear[year]) return;
    byYear[year] = {
      monthlySignups: Array(12).fill(0),
      monthlyPlanUses: Array(12).fill(0),
      annualPlanUses: Array(12).fill(0),
      annualSignups: Array(12).fill(0),
      monthlyEarnings: Array(12).fill(0),
      tier1Earnings: Array(12).fill(0),
      tier2Earnings: Array(12).fill(0),
      tier3Earnings: Array(12).fill(0),
    };
  };

  // Keep plan detection simple and strict: monthly_plan only
  const isMonthlyPlan = (d) => d?.plan === "monthly_plan";
  const isAnnualPlan = (d) => d?.plan === "annual_plan";

  // Determine tiers and build YEAR buckets from ALL subs (historical)
  const calculateCommissionAndTiers = (subscriptions) => {
    let totalEarnings = 0;
    let monthlyRevenue = 0;

    const byYear = {};

    const validSubs = subscriptions.filter((sub) => {
      const d = sub.data();
      return !(d.gifted === true || d.discountPercentage === 100 || d.amount === 0);
    });

    const sortedSubs = validSubs.slice().sort((a, b) => {
      const dateA = parseSubDate(a.data().createdAt) || new Date(0);
      const dateB = parseSubDate(b.data().createdAt) || new Date(0);
      return dateA - dateB;
    });

    const tier1Ids = sortedSubs.slice(0, 50).map((s) => s.id);
    const tier2Ids = sortedSubs.slice(50, 100).map((s) => s.id);
    const tier3Ids = sortedSubs.slice(100).map((s) => s.id);

    validSubs.forEach((sub) => {
      const subData = sub.data();
      const subDate = parseSubDate(subData.createdAt);
      if (!subDate) return;

      const year = subDate.getFullYear();
      const month = subDate.getMonth(); // 0..11
      ensureYear(byYear, year);

      const amountPaid = Number(subData.planDetails?.cost || 0);
      const commissionRate = tier1Ids.includes(sub.id) ? 0.2 : tier2Ids.includes(sub.id) ? 0.3 : 0.4;
      const commission = amountPaid * commissionRate;

      totalEarnings += commission;
      monthlyRevenue += isAnnualPlan(subData) ? commission / 12 : commission;

      // Keep existing behavior: monthlySignups counts ALL signups
      byYear[year].monthlySignups[month] += 1;

      if (isMonthlyPlan(subData)) byYear[year].monthlyPlanUses[month] += 1;
      if (isAnnualPlan(subData)) {
        byYear[year].annualPlanUses[month] += 1;
        byYear[year].annualSignups[month] += 1;
      }

      byYear[year].monthlyEarnings[month] += commission;

      if (tier1Ids.includes(sub.id)) byYear[year].tier1Earnings[month] += commission;
      else if (tier2Ids.includes(sub.id)) byYear[year].tier2Earnings[month] += commission;
      else byYear[year].tier3Earnings[month] += commission;
    });

    const currentYear = new Date().getFullYear();
    ensureYear(byYear, currentYear);

    return {
      total: totalEarnings,
      monthly: monthlyRevenue,
      referralsByTier: {
        tier1: tier1Ids.length,
        tier2: tier2Ids.length,
        tier3: tier3Ids.length,
      },
      currentTier: sortedSubs.length <= 50 ? 1 : sortedSubs.length <= 100 ? 2 : 3,
      byYear,

      // defaults (current year)
      monthlySignups: byYear[currentYear].monthlySignups,
      monthlyPlanUses: byYear[currentYear].monthlyPlanUses,
      annualPlanUses: byYear[currentYear].annualPlanUses,
      annualSignups: byYear[currentYear].annualSignups,
      monthlyEarnings: byYear[currentYear].monthlyEarnings,
      tier1Earnings: byYear[currentYear].tier1Earnings,
      tier2Earnings: byYear[currentYear].tier2Earnings,
      tier3Earnings: byYear[currentYear].tier3Earnings,

      tier1Ids,
      tier2Ids,
      tier3Ids,
    };
  };

  // Calculate commission totals for a given subs list using fixed tier membership
  const calculateCommissionUsingTiers = (subscriptions, tierIdsObj) => {
    let totalEarnings = 0;
    let monthlyRevenue = 0;

    subscriptions.forEach((sub) => {
      const subData = sub.data();
      if (subData.gifted === true || subData.discountPercentage === 100 || subData.amount === 0) return;

      const subDate = parseSubDate(subData.createdAt);
      if (!subDate) return;

      const amountPaid = Number(subData.planDetails?.cost || 0);
      const commissionRate = tierIdsObj.tier1Ids.includes(sub.id)
        ? 0.2
        : tierIdsObj.tier2Ids.includes(sub.id)
        ? 0.3
        : 0.4;

      const commission = amountPaid * commissionRate;

      totalEarnings += commission;
      monthlyRevenue += isAnnualPlan(subData) ? commission / 12 : commission;
    });

    return { total: totalEarnings, monthly: monthlyRevenue };
  };

  // NEW: active MONTHLY only (no annuals) tier breakdown + recurring total
  const countByTier = (subs, tierIdsObj) => {
    const ids = subs.map((s) => s.id);
    const t1 = ids.filter((id) => tierIdsObj.tier1Ids.includes(id)).length;
    const t2 = ids.filter((id) => tierIdsObj.tier2Ids.includes(id)).length;
    const t3 = ids.length - t1 - t2;
    return { tier1: t1, tier2: t2, tier3: t3 };
  };

  const calcMonthlyRecurringCommission = (subs, tierIdsObj) => {
    return subs.reduce((sum, s) => {
      const d = s.data();
      const amount = Number(d?.planDetails?.cost || 0);
      const rate = tierIdsObj.tier1Ids.includes(s.id) ? 0.2 : tierIdsObj.tier2Ids.includes(s.id) ? 0.3 : 0.4;
      return sum + amount * rate;
    }, 0);
  };

  useEffect(() => {
    if (useMockData) {
      setStats(mockData.stats);
      setCodes(mockData.codes);
      setPayouts(mockData.payouts);
      payoutsRef.current = mockData.payouts || [];
      return;
    }

    let unsubscribeActive = null;

    const loadAffiliateData = async () => {
      try {
        setError(null);

        // discount codes for this affiliate
        const codesQuery = query(collection(db, "discountCodes"), where("affiliateId", "==", viewingUserId));
        const codesSnapshot = await getDocs(codesQuery);
        const codeValues = codesSnapshot.docs.map((d) => d.data().code).filter(Boolean);

        if (codeValues.length === 0) {
          setCodes([]);
          setPayouts([]);
          setStats((prev) => ({
            ...prev,
            byYear: {},
            monthlySignups: Array(12).fill(0),
            monthlyPlanUses: Array(12).fill(0),
            annualPlanUses: Array(12).fill(0),
            annualSignups: Array(12).fill(0),
            monthlyEarnings: Array(12).fill(0),
            tier1Earnings: Array(12).fill(0),
            tier2Earnings: Array(12).fill(0),
            tier3Earnings: Array(12).fill(0),
            activeMonthlyReferrals: 0,
            activeMonthlyReferralsByTier: { tier1: 0, tier2: 0, tier3: 0 },
            activeMonthlyRecurringCommissionTotal: 0,
          }));
          return;
        }

        // payouts
        const payoutsQuery = query(collection(db, "payoutRequests"), where("affiliateId", "==", viewingUserId));
        const payoutsSnapshot = await getDocs(payoutsQuery);
        const payoutsData = payoutsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPayouts(payoutsData);
        payoutsRef.current = payoutsData;
        const getStatus = (p) => String(p?.status || "").trim().toLowerCase();

const pendingPayoutTotal = payoutsData
  .filter((p) => getStatus(p) === "pending")
  .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

        const totalEarningsCleared = payoutsData
          .filter((p) => p.status === "paid")
          .reduce((sum, p) => sum + (p.earningsCleared || 0), 0);

        const lifetimeEarnings = payoutsData
          .filter((p) => ["paid", "pending"].includes(p.status))
          .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

        // ALL subscriptions (historical) attributed to this affiliate
        const allSubsSnapshot = await getDocs(query(collection(db, "subscriptions")));
        const allSubs = allSubsSnapshot.docs.filter((d) => {
          const data = d.data();
          return codeValues.includes(data.planDetails?.discountApplied?.code) || codeValues.includes(data.giftCode);
        });

        // ACTIVE subscriptions (for live numbers)
        const activeSubsQuery = query(collection(db, "subscriptions"), where("status", "==", "active"));
        const activeSubsSnapshot = await getDocs(activeSubsQuery);
        const activeSubsInitial = activeSubsSnapshot.docs.filter((d) => {
          const data = d.data();
          return codeValues.includes(data.planDetails?.discountApplied?.code) || codeValues.includes(data.giftCode);
        });

        // Code usage counts (based on ALL subs)
        const codesWithUsage = codesSnapshot.docs.map((d) => {
          const codeData = d.data();
          const usageCount = allSubs.filter((sub) => {
            const subData = sub.data();
            return subData.planDetails?.discountApplied?.code === codeData.code || subData.giftCode === codeData.code;
          }).length;
          return { id: d.id, ...codeData, currentUses: usageCount };
        });
        setCodes(codesWithUsage);

        // tiers + historical
        const allCommissionData = calculateCommissionAndTiers(allSubs);

        // Use tiers derived from ALL subs for active commission calculations
        const tierIdsObj = {
          tier1Ids: allCommissionData.tier1Ids,
          tier2Ids: allCommissionData.tier2Ids,
          tier3Ids: allCommissionData.tier3Ids,
        };

        const activeCommissionDataInitial = calculateCommissionUsingTiers(activeSubsInitial, tierIdsObj);

        // ✅ NEW: active monthly-only (NO annuals)
        const activeMonthlySubsInitial = activeSubsInitial.filter((s) => isMonthlyPlan(s.data()));
        const activeMonthlyReferralsByTierInitial = countByTier(activeMonthlySubsInitial, tierIdsObj);
        const activeMonthlyRecurringCommissionTotalInitial = calcMonthlyRecurringCommission(
          activeMonthlySubsInitial,
          tierIdsObj
        );

        setStats((prev) => ({
          ...prev,
          totalEarnings: lifetimeEarnings,
          commissionTotal: allCommissionData.total,
          monthlyRevenue: activeCommissionDataInitial.monthly,
          availableForPayout: pendingPayoutTotal,
          lifetimeEarnings,

          referralsByTier: allCommissionData.referralsByTier,
          currentTier: allCommissionData.currentTier,

          byYear: allCommissionData.byYear,

          monthlySignups: allCommissionData.monthlySignups,
          monthlyPlanUses: allCommissionData.monthlyPlanUses,
          annualPlanUses: allCommissionData.annualPlanUses,
          annualSignups: allCommissionData.annualSignups,
          monthlyEarnings: allCommissionData.monthlyEarnings,
          tier1Earnings: allCommissionData.tier1Earnings,
          tier2Earnings: allCommissionData.tier2Earnings,
          tier3Earnings: allCommissionData.tier3Earnings,

          activeReferrals: activeSubsInitial.length,

          // ✅ NEW fields for the Monthly Payout Info box
          activeMonthlyReferrals: activeMonthlySubsInitial.length,
          activeMonthlyReferralsByTier: activeMonthlyReferralsByTierInitial,
          activeMonthlyRecurringCommissionTotal: activeMonthlyRecurringCommissionTotalInitial,
        }));

        // Real-time listener for ACTIVE subscriptions
        unsubscribeActive = onSnapshot(
          activeSubsQuery,
          (snapshot) => {
            const activeSubsLive = snapshot.docs.filter((d) => {
              const data = d.data();
              return codeValues.includes(data.planDetails?.discountApplied?.code) || codeValues.includes(data.giftCode);
            });

            const activeCommissionLive = calculateCommissionUsingTiers(activeSubsLive, tierIdsObj);

            const cleared = (payoutsRef.current || [])
              .filter((p) => p.status === "paid")
              .reduce((sum, p) => sum + (p.earningsCleared || 0), 0);

            // ✅ NEW: active monthly-only (NO annuals)
            const activeMonthlySubsLive = activeSubsLive.filter((s) => isMonthlyPlan(s.data()));
            const activeMonthlyReferralsByTierLive = countByTier(activeMonthlySubsLive, tierIdsObj);
            const activeMonthlyRecurringCommissionTotalLive = calcMonthlyRecurringCommission(
              activeMonthlySubsLive,
              tierIdsObj
            );

            setStats((prev) => ({
              ...prev,
              activeReferrals: activeSubsLive.length,
              monthlyRevenue: activeCommissionLive.monthly,
              availableForPayout: (payoutsRef.current || [])
  .filter((p) => String(p?.status || "").trim().toLowerCase() === "pending")
  .reduce((sum, p) => sum + (Number(p.amount) || 0), 0),

              // ✅ keep monthly-only display correct
              activeMonthlyReferrals: activeMonthlySubsLive.length,
              activeMonthlyReferralsByTier: activeMonthlyReferralsByTierLive,
              activeMonthlyRecurringCommissionTotal: activeMonthlyRecurringCommissionTotalLive,
            }));
          },
          (err) => console.error("Active subs snapshot error:", err)
        );
      } catch (err) {
        console.error("Error loading affiliate data:", err);
        setError("Failed to load affiliate data");
      }
    };

    if (viewingUserId) loadAffiliateData();
    return () => {
      if (unsubscribeActive) unsubscribeActive();
    };
  }, [viewingUserId, useMockData]);

  // keep payoutsRef in sync for snapshot callbacks
  useEffect(() => {
    payoutsRef.current = payouts;
  }, [payouts]);

  return (
    <DashboardLayout>
      <MDBox p={3}>
        {error && (
          <MDTypography color="error" variant="caption" fontWeight="medium">
            {error}
          </MDTypography>
        )}

        {isAdmin && (
          <MDBox mb={3}>
            <Card>
              <MDBox p={2}>
                <Select
                  fullWidth
                  value={selectedAffiliateId || ""}
                  onChange={(e) => setSelectedAffiliateId(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">View Own Dashboard</MenuItem>
                  {affiliates.map((affiliate) => (
                    <MenuItem key={affiliate.id} value={affiliate.id}>
                      {affiliate.email}
                    </MenuItem>
                  ))}
                </Select>
              </MDBox>
            </Card>
          </MDBox>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StatsCards stats={stats} />
          </Grid>

          <Grid item xs={12}>
            <TierProgress currentCount={stats.activeReferrals} />
          </Grid>

          <Grid item xs={12}>
            <CodeList codes={codes} />
          </Grid>

          <Grid item xs={12}>
            {/* Charts read data.earnings.byYear */}
            <Charts data={{ earnings: stats, codes }} />
          </Grid>

          <Grid item xs={12}>
            <PayoutTable payouts={payouts} isAdmin={isAdmin} paypalEmail={paypalEmail} />
          </Grid>

          <Grid item xs={12}>
            <PayoutRequest
              availableAmount={stats.availableForPayout}
              stats={stats}
              minimumPayout={10}
              userId={viewingUserId}
              isAdmin={isAdmin}
              paypalEmail={paypalEmail}
              setPaypalEmail={setPaypalEmail}
            />
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default AffiliateDashboard;